import type {
  CreateOpportunityTaskRequest,
  OpportunityTask,
  OpportunityTaskStatusesResponse,
  OpportunityTasksResponse,
  UpdateOpportunityTaskRequest,
} from 'backend/types'
import { apiFetch } from '..'

// Re-export OpportunityTask for convenience
export type { OpportunityTask }

/**
 * 自分のタスク一覧取得API
 *
 * ログイン中のメンバーに割り当てられたタスク一覧を取得します。
 *
 * @param params - クエリパラメータ
 * @param params.offset - オフセット（デフォルト: 0）
 * @param params.limit - 上限（オプション）
 * @returns タスク一覧レスポンス
 * @throws ApiError - 認証失敗（403）、サーバーエラー（500）
 */
export async function getMyTasks(params?: { offset?: number; limit?: number }): Promise<OpportunityTasksResponse> {
  const queryParams = new URLSearchParams()
  if (params?.offset !== undefined) {
    queryParams.append('offset', params.offset.toString())
  }
  if (params?.limit !== undefined) {
    queryParams.append('limit', params.limit.toString())
  }

  const queryString = queryParams.toString()
  const url = `/api/opportunity/tasks${queryString ? `?${queryString}` : ''}`

  return apiFetch<OpportunityTasksResponse>(url, { method: 'GET' })
}

/**
 * オポチュニティタスク一覧取得API
 *
 * @param opportunityId - オポチュニティID
 * @param params - クエリパラメータ
 * @returns タスク一覧レスポンス
 * @throws ApiError
 */
export async function getOpportunityTasks(
  opportunityId: number,
  params?: {
    taskStatusId?: number
    memberId?: number
    offset?: number
    limit?: number
  },
): Promise<OpportunityTasksResponse> {
  const queryParams = new URLSearchParams()
  if (params?.taskStatusId !== undefined) {
    queryParams.append('taskStatusId', params.taskStatusId.toString())
  }
  if (params?.memberId !== undefined) {
    queryParams.append('memberId', params.memberId.toString())
  }
  if (params?.offset !== undefined) {
    queryParams.append('offset', params.offset.toString())
  }
  if (params?.limit !== undefined) {
    queryParams.append('limit', params.limit.toString())
  }

  const queryString = queryParams.toString()
  const url = `/api/opportunity/${opportunityId}/tasks${queryString ? `?${queryString}` : ''}`

  return apiFetch<OpportunityTasksResponse>(url, { method: 'GET' })
}

/**
 * オポチュニティタスク作成API
 *
 * @param opportunityId - オポチュニティID
 * @param data - タスク作成データ
 * @returns 作成されたタスク
 * @throws ApiError
 */
export async function createOpportunityTask(
  opportunityId: number,
  data: CreateOpportunityTaskRequest,
): Promise<OpportunityTask> {
  return apiFetch<OpportunityTask>(`/api/opportunity/${opportunityId}/task`, {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

/**
 * タスク更新API
 *
 * タスクの情報（担当者、ステータス、タスク内容、期限等）を更新します。
 *
 * @param opportunityId - オポチュニティID
 * @param taskId - タスクID
 * @param data - タスク更新データ
 * @returns 更新されたタスク
 * @throws ApiError - 認証失敗（403）、権限不足（403）、タスク未発見（404）、サーバーエラー（500）
 */
export async function updateOpportunityTask(
  opportunityId: number,
  taskId: number,
  data: UpdateOpportunityTaskRequest,
): Promise<OpportunityTask> {
  return apiFetch<OpportunityTask>(`/api/opportunity/${opportunityId}/task/${taskId}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  })
}

/**
 * タスク削除API
 *
 * タスクを削除します（論理削除）。
 *
 * @param opportunityId - オポチュニティID
 * @param taskId - タスクID
 * @returns Promise<void> - 削除成功時は204 No Contentで空レスポンス
 * @throws ApiError - 認証失敗（403）、権限不足（403）、タスク未発見（404）、サーバーエラー（500）
 */
export async function deleteOpportunityTask(opportunityId: number, taskId: number): Promise<void> {
  return apiFetch<void>(`/api/opportunity/${opportunityId}/task/${taskId}`, {
    method: 'DELETE',
  })
}

/**
 * タスクステータス一覧取得API
 *
 * タスクのステータスマスターデータ（未着手、進行中、完了等）を取得します。
 * マスターデータなので全件取得します。
 *
 * @returns タスクステータス一覧レスポンス
 * @throws ApiError - 認証失敗（403）、サーバーエラー（500）
 */
export async function getTaskStatuses(): Promise<OpportunityTaskStatusesResponse> {
  return apiFetch<OpportunityTaskStatusesResponse>('/api/opportunity/task/statuses', { method: 'GET' })
}
